import React, { createContext, useContext, useState, useCallback } from 'react';

const ModalContext = createContext(null);

export function useModal() {
  const ctx = useContext(ModalContext);
  if (!ctx) throw new Error('useModal must be used within ModalProvider');
  return ctx;
}

export function ModalProvider({ children }) {
  const [stack, setStack] = useState([]);

  const push = useCallback((type, payload, onResult) => {
    const id = `modal-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setStack((prev) => [...prev, { id, type, payload, onResult }]);
    return id;
  }, []);

  const dismiss = useCallback((id, result) => {
    setStack((prev) => {
      const modal = prev.find((m) => m.id === id);
      modal?.onResult?.(result);
      return prev.filter((m) => m.id !== id);
    });
  }, []);

  const alert = useCallback(
    (message, title) =>
      new Promise((resolve) => {
        push('alert', { message, title }, (r) => resolve(r));
      }),
    [push]
  );

  const confirm = useCallback(
    (message, title) =>
      new Promise((resolve) => {
        push('confirm', { message, title }, (r) => resolve(r));
      }),
    [push]
  );

  const prompt = useCallback(
    (message, defaultValue, title, options = {}) =>
      new Promise((resolve) => {
        push('prompt', { message, defaultValue, title, ...options }, (r) => resolve(r));
      }),
    [push]
  );

  const passwordPrompt = useCallback(
    (message, title) =>
      new Promise((resolve) => {
        push('prompt', { message, title, type: 'password' }, (r) => resolve(r));
      }),
    [push]
  );

  const value = { alert, confirm, prompt, passwordPrompt, dismiss };

  return (
    <ModalContext.Provider value={value}>
      {children}
      <ModalOverlay stack={stack} onDismiss={dismiss} />
    </ModalContext.Provider>
  );
}

function ModalOverlay({ stack, onDismiss }) {
  const active = stack[stack.length - 1];
  if (!active) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" role="dialog" aria-modal="true">
      <div className="absolute inset-0" style={{ background: 'rgba(5,6,15,0.6)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }} onClick={() => onDismiss(active.id, active.type === 'prompt' ? null : false)} />
      <div className="relative w-full max-w-md mx-4 overflow-hidden animate-in fade-in zoom-in duration-200" style={{ background: 'var(--surface)', borderRadius: 16, boxShadow: 'var(--shadow-2)', border: '1px solid var(--glass-border)' }}>
        {active.type === 'alert' && <AlertModal modal={active} onDismiss={onDismiss} />}
        {active.type === 'confirm' && <ConfirmModal modal={active} onDismiss={onDismiss} />}
        {active.type === 'prompt' && <PromptModal modal={active} onDismiss={onDismiss} />}
      </div>
    </div>
  );
}

function AlertModal({ modal, onDismiss }) {
  const { title, message } = modal.payload;
  return (
    <>
      <div style={{ padding: 24 }}>
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 flex items-center justify-center" style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(96,165,250,0.12)' }}>
            <svg className="w-5 h-5" style={{ color: 'var(--info)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            {title && <h3 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-1)', marginBottom: 4 }}>{title}</h3>}
            <p className="whitespace-pre-wrap leading-relaxed" style={{ fontSize: 14, color: 'var(--text-2)' }}>{message}</p>
          </div>
        </div>
      </div>
      <div className="flex justify-end" style={{ padding: '0 24px 20px' }}>
        <button
          onClick={() => onDismiss(modal.id, true)}
          style={{ padding: '10px 20px', fontSize: 14, fontWeight: 500, borderRadius: 12, background: 'var(--glass-strong)', color: 'var(--text-1)', border: '1px solid var(--glass-border)' }}
          autoFocus
        >
          OK
        </button>
      </div>
    </>
  );
}

function ConfirmModal({ modal, onDismiss }) {
  const { title, message } = modal.payload;
  return (
    <>
      <div style={{ padding: 24 }}>
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 flex items-center justify-center" style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(251,191,36,0.12)' }}>
            <svg className="w-5 h-5" style={{ color: 'var(--warning)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.88-.833-2.65 0L4.162 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            {title && <h3 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-1)', marginBottom: 4 }}>{title}</h3>}
            <p className="whitespace-pre-wrap leading-relaxed" style={{ fontSize: 14, color: 'var(--text-2)' }}>{message}</p>
          </div>
        </div>
      </div>
      <div className="flex gap-3 justify-end" style={{ padding: '0 24px 20px' }}>
        <button
          onClick={() => onDismiss(modal.id, false)}
          style={{ padding: '10px 20px', fontSize: 14, fontWeight: 500, borderRadius: 12, background: 'var(--glass-strong)', color: 'var(--text-1)', border: '1px solid var(--glass-border)' }}
          autoFocus
        >
          Cancel
        </button>
        <button
          onClick={() => onDismiss(modal.id, true)}
          style={{ padding: '10px 20px', fontSize: 14, fontWeight: 600, color: '#fff', borderRadius: 12, background: 'var(--danger)', boxShadow: '0 4px 20px rgba(248,113,113,0.2)' }}
        >
          Confirm
        </button>
      </div>
    </>
  );
}

function PromptModal({ modal, onDismiss }) {
  const { title, message, defaultValue = '', type = 'text' } = modal.payload;
  const [val, setVal] = useState(defaultValue);
  return (
    <>
      <div style={{ padding: 24 }}>
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 flex items-center justify-center" style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(139,92,246,0.12)' }}>
            <svg className="w-5 h-5" style={{ color: 'var(--accent-1)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            {title && <h3 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-1)', marginBottom: 4 }}>{title}</h3>}
            <p className="whitespace-pre-wrap" style={{ fontSize: 14, color: 'var(--text-2)', marginBottom: 12 }}>{message}</p>
            <input
              type={type === 'password' ? 'password' : 'text'}
              value={val}
              onChange={(e) => setVal(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && onDismiss(modal.id, val)}
              className="w-full"
              style={{ padding: '8px 12px', fontSize: 14, border: '1px solid var(--border-strong)', borderRadius: 10, background: 'var(--surface)', color: 'var(--text-1)', outline: 'none' }}
              autoFocus
            />
          </div>
        </div>
      </div>
      <div className="flex gap-3 justify-end" style={{ padding: '0 24px 20px' }}>
        <button
          onClick={() => onDismiss(modal.id, null)}
          style={{ padding: '10px 20px', fontSize: 14, fontWeight: 500, borderRadius: 12, background: 'var(--glass-strong)', color: 'var(--text-1)', border: '1px solid var(--glass-border)' }}
        >
          Cancel
        </button>
        <button
          onClick={() => onDismiss(modal.id, val)}
          style={{ padding: '10px 20px', fontSize: 14, fontWeight: 600, color: '#fff', borderRadius: 12, background: 'var(--accent-1)', boxShadow: '0 4px 20px rgba(139,92,246,0.3)' }}
        >
          OK
        </button>
      </div>
    </>
  );
}
